import { useState, useEffect } from 'react';
import { Catalogue } from '@ynap/api-utils';
import { pdpSelectors } from '@ynap/product-details-state';
import { productSelectors } from '@ynap/product-state';
import { raygun } from '@ynap/clientside-monitoring';
import { useConfig, useAppState } from '@ynap/immutables-provider';
import 'cross-fetch/polyfill';

const { getSelectedProductImages, getSelectedProductPrice } = pdpSelectors;
const { getProductLinkByPartNumber, getProductImageFromTemplate, getMappedProducts } = productSelectors;
const MAX_RECOMMENDATIONS = 16;
const RECS_CATEGORY_PATH = 'just-in';
const RECS_MENS_CATEGORY_PATH = 'mens/just-in';

const configSelector = (config) => ({
    baseUrl: config.global.services.commerce.baseUrl,
    brandId: config.global.application.brandId,
    clientId: config.global.services.commerce.clientId,
    availableImageSizes: config.head.availableImageSizes,
    imageQuality: config.head.imageQualityPercentage,
    pdpUrlTemplate: config.global.urlConfig.pdp.construct,
    maxRecommendations: config.head.pdp.maxRecommendations,
    outfitViewFirstFallback: config.head.pdp?.recommendations?.outfitViewFirstFallback,
    outfitViewSecondFallback: config.head.pdp?.recommendations?.outfitViewSecondFallback,
    imagesphereAspectRatio: config.head.aspectRatio?.imagesphere,
    useIdentifier: config.head.useIdentifier,
});

export const useCategoryRecs = ({ partNumber, messages, isMenswearOnTON }) => {
    const [recommendations, setRecommendations] = useState({});

    const { country, langISO, locale } = useAppState();

    const appProductConfig = { ...useConfig(configSelector), locale };
    const { baseUrl, brandId, clientId } = appProductConfig;

    useEffect(() => {
        let requests = getRecommendationRequests(baseUrl, brandId, country, clientId, langISO, isMenswearOnTON);

        const fetchRecommendations = async () => {
            const normalizedData = await fetchData(requests, appProductConfig, messages);
            setRecommendations(normalizedData);
        };

        fetchRecommendations();
    }, [partNumber]);

    return recommendations;
};

//return an array of Requests of recommendation api-utils
const getRecommendationRequests = (baseUrl, brandId, country, clientId, locale, isMenswearOnTON) => {
    const options = {
        attrs: 'true',
        locale,
        pageNumber: 1,
        pageSize: 96,
    };

    return Catalogue.getCategoryBySeoPath({
        baseUrl,
        brandId,
        country,
        path: isMenswearOnTON ? RECS_MENS_CATEGORY_PATH : RECS_CATEGORY_PATH,
        options,
        clientId,
    });
};

//TODO extract to shared library: https://jira.nap/browse/ISPW-463
//combine requests and normalize function together and return normalized recommendation data
const fetchData = async (requests, appProductConfig, messages) => {
    const jsonResponses = await singleCall(requests);
    const slicedRecs = jsonResponses[0]?.products.slice(0, MAX_RECOMMENDATIONS);
    const pageType = 'SEARCH';

    return getMappedProducts(slicedRecs, appProductConfig, pageType, messages, appProductConfig?.useIdentifier);
};

const singleCall = async (request) => {
    const calls = [fetch(request)];
    let allJson, allRes;

    try {
        allRes = await Promise.all(calls);
    } catch (error) {
        raygun &&
            raygun.helpers.agent('send', {
                error,
                tags: ['recommendations'],
            });
        // eslint-disable-next-line no-console
        return console.log('fetching error', error);
    }

    try {
        allJson = await Promise.all(allRes.map((a) => a.json()));
    } catch (error) {
        raygun &&
            raygun.helpers.agent('send', {
                error,
                tags: ['recommendations'],
            });
        // eslint-disable-next-line no-console
        return console.log('json parsing error', error);
    }
    return allJson;
};

export const getRecommendedProducts = (
    recommendationsResponse,
    availableImageSizes,
    imageQuality,
    pdpUrlTemplate,
    locale,
    recommendationName,
    imageTemplate,
    imageViews,
    outfitViewFirstFallback,
    outfitViewSecondFallback,
    imagesphereAspectRatio,
) => {
    const recommendationData = recommendationsResponse?.MarketingSpotData[0]?.baseMarketingSpotActivityData;
    if (!recommendationData) {
        return null;
    }

    const products = recommendationData.reduce((acc, { x_catalogEntryView: productData, productPartNumber }) => {
        if (!productData || acc.length == maxRecommendations) return acc;
        const { designerName, designerIdentifier, masterCategory, name, shortDescription, badges = [] } = productData;

        const description = shortDescription || name;
        const image = getImages(productData, availableImageSizes, imageQuality, imagesphereAspectRatio);
        const price = getSelectedProductPrice(productData);
        const href = getProductLinkByPartNumber(pdpUrlTemplate, locale, productData.seo, productPartNumber);

        return [
            ...acc,
            { image, price, designer: designerName, designerIdentifier, description, partNumber: productPartNumber, href, badge: badges[0], masterCategory },
        ];
    }, []);

    if (recommendationName === 'WIW_ProductRec') {
        return [
            {
                products,
                outfitImage: getOutfitImage(
                    imageTemplate,
                    availableImageSizes,
                    imageQuality,
                    imageViews,
                    outfitViewFirstFallback,
                    outfitViewSecondFallback,
                    imagesphereAspectRatio,
                ),
            },
        ];
    }
    return products;
};

export const getOutfitImage = (
    imageTemplate,
    availableImageSizes,
    imageQuality,
    imageViews,
    outfitViewFirstFallback,
    outfitViewSecondFallback,
    imagesphereAspectRatio,
) => {
    const outfitView = imageViews.includes(outfitViewFirstFallback)
        ? outfitViewFirstFallback
        : imageViews.includes(outfitViewSecondFallback)
          ? outfitViewSecondFallback
          : imageViews[0];
    const { url, srcSet } = getProductImageFromTemplate(imageTemplate, outfitView, availableImageSizes, imageQuality, null, imagesphereAspectRatio);
    return {
        alt: '',
        url,
        srcSet,
    };
};

const getImages = (productData, availableImageSizes, imageQuality, imagesphereAspectRatio) => {
    const images = getSelectedProductImages(productData, productData, availableImageSizes, imageQuality, null, imagesphereAspectRatio);

    return {
        alt: images[0]?.alt,
        primary: images[0]?.url,
        primarySrcset: images[0]?.srcSet,
        secondary: images[1]?.url,
        secondarySrcset: images[1]?.srcSet,
    };
};
